import React, { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Value } from 'react-calendar/dist/cjs/shared/types';
import Scrollbars from 'react-custom-scrollbars-2';
import moment from 'moment';
import { faSearch, faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { colorStack } from '../../../styleHelpers/colors';
import { Button, EButtonSize, EButtonTypeSchema } from '../Buttons/NewButton';
import { IItem } from '../../../entities/IFilters';
import { fontSizeAndHeight } from '../../../styleHelpers/fontSizes';
import { EFilterType } from '../../../entities/IGlobal';
import { ESpinnerSize, Spinner } from '../Spinner/Spinner';
import { TextComponent } from '../Inputs/TextComponent';
import { EButtonsTypes } from '../Pickers/CorePicker';
import { removeArrDuplicates } from '../../../tools/arrayTools';
import ScrollBox from '../ScrollBox/ScrollBox';
import { IValue } from '../../../entities/IPickers';
import IntlMessage from '../IntlMessage';
import DateRangeFilter from '../ColoredDatagrid/DateRange/DateRangeFilter';
import { SingleResultElement } from './SingleResultElement';
import { BooleanResultOptions } from './BooleanResultOptions';

const Wrapper = styled.div<{ autoWidth?: boolean }>`
    border-radius: 8px;
    width: 346px;
    overflow: hidden;
    background: ${colorStack.white};
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.16);
    ${props => props.autoWidth && css`
        width: auto;
    `}
`;

const SearchBox = styled.div`
    padding: .5rem;
    background: ${colorStack.whiteGrey};
    input {
        background: ${colorStack.white};
    }
`;

const Results = styled.div`
`;

const ButtonsWrapper = styled.div`
    display: flex;
    padding: .5rem;
    background: ${colorStack.whiteGrey};
    align-items: center;
    justify-content: flex-end;
    gap: .5rem;
`;

const NoResult = styled.div`
    height: 62px;
    display: flex;
    align-items: center;
    justify-content: center;
    ${fontSizeAndHeight[16]};
    color: ${colorStack.content};
`;

const AddingButton = styled.div`
    color: ${colorStack.middleBlue};
    ${fontSizeAndHeight[16]};
    font-weight: 500;
    height: 48px;
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    position: relative;
    margin: 0 auto;
    z-index: 9;
    cursor:pointer;
    svg {
        margin: 0 0.5rem 0 0;
    }
`;

const DoubleCreationWrapper = styled(AddingButton as any)`
    align-items: center;
    flex-direction: column;
    justify-content: space-between;
    height: 70px;
    &:hover {
        background: unset;
    }
`;

const SingleCreateWrapper = styled.div`
    width: 100%;
    text-align: center;
    position: relative;
    z-index: 9;
    cursor: pointer;
`;

interface IProps {
    items: IItem[];
    selectedItems: IItem[];
    dateSelectedItems?: {
        fromDate: Date;
        toDate: Date;
    };
    placeholder?: string;
    type: EFilterType;
    translationKey: string;
    multiSelect?: boolean;
    hideButtons?: boolean;
    loading?: boolean;
    buttonsType?: EButtonsTypes;
    className?: string;
    onSelectItem(item: IItem);
    onClear();
    onSave();
    getData?(text: string);
    userFormHandler?();
    companyFormHandler?();
    outsideGetSearchValue?(value: string);
}

let resultsDebounce: ReturnType<typeof setTimeout>;
let debounceTimout: ReturnType<typeof setTimeout> = undefined;

export const ResultBox: FC<React.PropsWithChildren<IProps>> = props => {
    const [searchValue, setSearchValue] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [dateValue, setDateValue] = useState<Value>([undefined, undefined]);
    const [displayedElements, setDisplayedElements] = useState<IItem[]>([]);
    const scrollBarsRef = useRef<Scrollbars>();

    useEffect(() => {
        setLoading(props.loading);
    }, [props.loading]);

    const changeHandler = (value: string, item: IValue, e: React.ChangeEvent<HTMLInputElement>) => {
        setPage(0);
        setLoading(true);
        clearTimeout(resultsDebounce);
        e.stopPropagation();
        setSearchValue(value);
        props.outsideGetSearchValue?.(value);
        clearTimeout(debounceTimout);
        resultsDebounce = setTimeout(async () => {
            props.getData?.(value);
        }, 1000);
        if (!props.getData) {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    };

    useEffect(() => {
        if (props.dateSelectedItems) {
            setDateValue([props.dateSelectedItems?.fromDate, props.dateSelectedItems?.toDate]);
        }
    }, [props.dateSelectedItems]);

    const selectItem = (elem: IItem) => {
        props.onSelectItem(elem);
    };

    const onClick = (e) => {
        e.stopPropagation();
    };

    const normalizeDateToStartOfDay = (date: Date | null): Date | null => {
        if (!date) return null;
        return moment(date).startOf('day').toDate();
    };

    const normalizeDateToEndOfDay = (date: Date | null): Date | null => {
        if (!date) return null;
        return moment(date).endOf('day').toDate();
    };

    const dateChangeHandler = (value: Value) => {
        setDateValue(value);

        const normalizedFromDate = value[0] ? normalizeDateToStartOfDay(value[0]) : null;
        const normalizedToDate = value[1] ? normalizeDateToEndOfDay(value[1]) : null;

        const dateRanges = {
            text: `${normalizedFromDate ? moment(normalizedFromDate).format('DD MMM YYYY') : ''} - ${normalizedToDate ? moment(normalizedToDate).format('DD MMM YYYY') : ''}`,
            id: 'dateRanges',
            visible: true,
            data: {
                isDate: true,
                fromDate: normalizedFromDate,
                toDate: normalizedToDate,
                text: `${normalizedFromDate ? moment(normalizedFromDate).format('DD MMM YYYY') : ''} - ${normalizedToDate ? moment(normalizedToDate).format('DD MMM YYYY') : ''}`,
                id: 'dateRanges'
            },
            translationKey: undefined
        };
        selectItem(dateRanges);
    };

    const clearHandler = () => {
        setSearchValue('');
        setPage(0);
        props.outsideGetSearchValue?.('');
        setLoading(true);
        clearTimeout(debounceTimout);
        props.getData?.('');
        debounceTimout = setTimeout(() => {
            setLoading(false);
        }, 500);
    };

    const getRef = (scrollRef: Scrollbars) => {
        scrollBarsRef.current = scrollRef;
    };

    const filteredElements = useMemo(() => {
        const nameFilter = props.items?.filter(elem => elem?.text?.toLowerCase()?.includes(searchValue.toLowerCase())) || [];
        const emailFilter = props.items?.filter(elem => elem?.data?.emailContact?.toLowerCase()?.includes(searchValue.toLowerCase())) || [];
        return removeArrDuplicates([...nameFilter, ...emailFilter]);
    }, [props.items, searchValue]);

    const getDisplayedElements = (pageNumber) => {
        const newDisplayedElements = [...filteredElements];
        setDisplayedElements(newDisplayedElements.slice(0, 50 + pageNumber * 50));
    };

    useEffect(() => {
        getDisplayedElements(page);
    }, [page, filteredElements.length]);

    const getMoreResults = () => {
        setPage((prevState) => prevState + 1);
    };

    return (
        <Wrapper className={props.className} autoWidth={props.type === EFilterType.date}>
            {props.type !== EFilterType.date &&
                <SearchBox>
                    <TextComponent
                        value={searchValue}
                        autoFocus
                        placeholder={props.placeholder}
                        onClearClick={clearHandler}
                        onClick={onClick}
                        onChange={changeHandler}
                        leftIco={faSearch}
                    />
                </SearchBox>
            }
            <Results>
                {props.type === EFilterType.date && (
                    <DateRangeFilter
                        onChange={dateChangeHandler}
                        value={dateValue}
                    />
                )}
                {props.type === EFilterType.boolean && (
                    <BooleanResultOptions
                        items={props.items}
                        selectedItems={props.selectedItems ?? []}
                        translationKey={props.translationKey}
                        onSelectItem={selectItem}
                    />
                )}
                {props.type !== EFilterType.date && props.type !== EFilterType.boolean && (
                    <ScrollBox
                        hasMore={filteredElements.length > 0 && (filteredElements?.length > displayedElements?.length)}
                        getRef={getRef}
                        withoutChildrenCount
                        onScrollResults={getMoreResults}
                        autoHeightMax={230}
                        autoHeight
                    >
                        {
                            <>
                                {loading ? (
                                    <>
                                        <br />
                                        <Spinner size={ESpinnerSize.SMALL} />
                                        <br />
                                    </>
                                ) : (
                                    <div>
                                        {displayedElements?.length > 0 ? (
                                            <>
                                                {displayedElements?.map(elem =>
                                                    <Fragment key={elem.id}>
                                                        <SingleResultElement
                                                            elem={elem}
                                                            multiSelect={props.multiSelect}
                                                            active={!!props.selectedItems?.find(selectedItem =>
                                                                selectedItem.id === elem.id
                                                                || selectedItem.data?.childId === elem.id
                                                                || selectedItem.id === elem.data?.childId
                                                            )}
                                                            selectItem={selectItem}
                                                            type={elem.data?.type?.toLocaleLowerCase() || props.type.toLocaleLowerCase()}
                                                            translationKey={props.translationKey}
                                                        />
                                                    </Fragment>
                                                )}
                                            </>
                                        ) : (
                                            <NoResult>
                                                <IntlMessage id="global.noResultsFound" />
                                            </NoResult>
                                        )}
                                    </div>
                                )}
                            </>
                        }
                    </ScrollBox>
                )}
            </Results>
            <ButtonsWrapper>
                {(!props.hideButtons && !loading && filteredElements?.length === 0) &&
                    <>
                        {props.buttonsType === EButtonsTypes.All
                            ? <DoubleCreationWrapper>
                                <SingleCreateWrapper onClick={props.userFormHandler}>
                                    <FontAwesomeIcon icon={faPlusCircle} />
                                    <IntlMessage id="searchInput.button.createPerson" />
                                </SingleCreateWrapper>
                                <SingleCreateWrapper onClick={props.companyFormHandler}>
                                    <FontAwesomeIcon icon={faPlusCircle} />
                                    <IntlMessage id="searchInput.button.createLegalEntity" />
                                </SingleCreateWrapper>
                            </DoubleCreationWrapper>
                            :
                            <>
                                {props.buttonsType === EButtonsTypes.Person &&
                                    <AddingButton onClick={props.userFormHandler}>
                                        <FontAwesomeIcon icon={faPlusCircle} />
                                        <IntlMessage id="searchInput.button.createPerson" />
                                    </AddingButton>
                                }
                                {(props.buttonsType === EButtonsTypes.Company) &&
                                    <AddingButton onClick={props.companyFormHandler}>
                                        <FontAwesomeIcon icon={faPlusCircle} />
                                        <IntlMessage id="searchInput.button.createLegalEntity" />
                                    </AddingButton>
                                }
                            </>
                        }
                    </>
                }
                {(filteredElements?.length > 0 || props.type === EFilterType.date || props.type === EFilterType.boolean) &&
                    <>
                        <Button
                            size={EButtonSize.SMALL}
                            typeSchema={EButtonTypeSchema.TERTIARY}
                            onClick={props.onClear}
                            disabled={props.type === EFilterType.date ? (!dateValue[0] && !dateValue[1]) : (!props.selectedItems || props.selectedItems?.length === 0)}
                        >
                            <IntlMessage id="filters.clear" />
                        </Button>
                        <Button
                            size={EButtonSize.SMALL}
                            onClick={props.onSave}
                            disabled={props.type === EFilterType.date ? (!dateValue[0] && !dateValue[1]) : (!props.selectedItems || props.selectedItems?.length === 0)}
                        >
                            <IntlMessage id="filters.apply" />
                        </Button>
                    </>
                }
            </ButtonsWrapper>
        </Wrapper>
    );
};